import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Spinner,
  Stack,
} from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useCreateOrEditExercise from "../../hooks/api/exercise/useCreateOrEditExercise";
import useGetVideos from "../../hooks/api/video/useGetVideos";
import { Exercise } from "../../models/Exercise";
import VideoPlayer from "../VideoPlayer";

type FormValues = {
  name: string;
  description: string;
  position: number;
  homeworkDeadline: string;
  hasSubtitleExercise: boolean;
  hasDubbingExercise: boolean;
  hasVoiceOverExercise: boolean;
  hasCaptionExercise: boolean;
  hasAudioDescriptionExercise: boolean;
  videoId: string;
};

type Props = {
  exercise?: Exercise;
  lessonId?: string;
  onSuccess?: (exerciseId: number) => void;
  onError?: () => void;
};

export default function ExerciseForm({
  exercise,
  lessonId,
  onSuccess,
  onError,
}: Props) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, isValid, isSubmitted },
  } = useForm<FormValues>({
    defaultValues: {
      name: exercise?.name || "",
      description: exercise?.description || "",
      position: exercise?.position,
      homeworkDeadline: exercise?.homeworkDeadline,
      hasSubtitleExercise: exercise?.hasSubtitleExercise,
      hasDubbingExercise: exercise?.hasDubbingExercise,
      hasVoiceOverExercise: exercise?.hasVoiceOverExercise,
      hasCaptionExercise: exercise?.hasCaptionExercise,
      hasAudioDescriptionExercise: exercise?.hasAudioDescriptionExercise,
      videoId: exercise?.video.id,
    },
  });

  const { data: videos, isLoading: areVideosLoading } = useGetVideos();

  const approvedVideos = useMemo(
    () => videos?.content.filter(({ status }) => status === "APPROVED") ?? [],
    [videos?.content],
  );

  const { mutateAsync: createOrEditExercise, isPending } =
    useCreateOrEditExercise(lessonId ?? "");

  const onSubmit = useCallback(
    (values: FormValues) => {
      createOrEditExercise({
        exercise: {
          id: exercise?.id,
          ...values,
          homeworkDeadline: new Date(values.homeworkDeadline).toISOString(),
        },
        operation: exercise ? "edit" : "create",
      })
        .then((response) => onSuccess?.(response.id))
        .catch(onError);
    },
    [createOrEditExercise, exercise, onError, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl
        isInvalid={!isValid && isSubmitted}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <div>
          <FormLabel htmlFor="name">{t("forms.name")}</FormLabel>
          <Input
            id="name"
            placeholder={t("forms.name")}
            {...register("name", { required: t("forms.validation.required") })}
          />
          <FormErrorMessage>
            {errors.name && errors.name.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="description">{t("forms.description")}</FormLabel>
          <Input
            id="description"
            placeholder={t("forms.description")}
            {...register("description", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="position">{t("forms.position")}</FormLabel>
          <Input
            id="position"
            placeholder={t("forms.position")}
            {...register("position", {
              required: t("forms.validation.required"),
            })}
            type={"number"}
          />
          <FormErrorMessage>
            {errors.position && errors.position.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="homeworkDeadline">
            {t("forms.homeworkDeadline")}
          </FormLabel>
          <Input
            id="homeworkDeadline"
            placeholder={t("forms.homeworkDeadline")}
            {...register("homeworkDeadline", {
              required: t("forms.validation.required"),
            })}
            type={"datetime-local"}
          />
          <FormErrorMessage>
            {errors.homeworkDeadline && errors.homeworkDeadline.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="videoId">{t("forms.video")}</FormLabel>
          {approvedVideos.length ? (
            <Select
              id="videoId"
              {...register("videoId", {
                required: t("forms.validation.required"),
              })}
            >
              <option value="">-</option>
              {approvedVideos.map((video) => (
                <option key={video.id} value={video.id}>
                  {video.title} - {video.description}
                </option>
              ))}
            </Select>
          ) : (
            <Spinner color="primary.DEFAULT" />
          )}
          <FormErrorMessage>
            {errors.videoId && errors.videoId.message}
          </FormErrorMessage>
        </div>
        {watch("videoId") && (
          <VideoPlayer
            url={approvedVideos.find((v) => v.id == watch("videoId"))?.url}
          />
        )}
        <div>
          <FormLabel>{t("forms.enabled_exercises")}</FormLabel>
          <CheckboxGroup>
            <Stack spacing={[1, 5]} direction={["column", "row"]}>
              <Checkbox {...register("hasDubbingExercise")}>
                {t("common.labels.dubbing")}
              </Checkbox>
              <Checkbox {...register("hasSubtitleExercise")}>
                {t("common.labels.subtitle")}
              </Checkbox>
              <Checkbox {...register("hasVoiceOverExercise")}>
                {t("common.labels.voice_over")}
              </Checkbox>
              <Checkbox {...register("hasCaptionExercise")}>
                {t("common.labels.caption")}
              </Checkbox>
              <Checkbox {...register("hasAudioDescriptionExercise")}>
                {t("common.labels.audio_description")}
              </Checkbox>
            </Stack>
          </CheckboxGroup>
        </div>
      </FormControl>
      <Button
        mt={4}
        bg="primary.DEFAULT"
        color="white"
        disabled={areVideosLoading}
        isLoading={isPending}
        type="submit"
      >
        {t("common.buttons.save")}
      </Button>
    </form>
  );
}
